import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPut } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { EmptyState } from './ListView';
import { canOperate } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState({});
  const [draft, setDraft] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    Promise.all([
      apiGet('/agents'),
      apiGet('/pm/members').catch(() => []),
    ])
      .then(([agentList, memberList]) => {
        const list = Array.isArray(agentList) ? agentList : [];
        setAgents(list);
        setMembers(Array.isArray(memberList) ? memberList : []);
        const init = {};
        list.forEach((a) => { init[a.id] = a.plane_member_id || ''; });
        setDraft(init);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveMapping(agentId) {
    setSaving((s) => ({ ...s, [agentId]: true }));
    try {
      const memberId = draft[agentId] || null;
      const member = members.find((m) => m.id === memberId);
      await apiPut(`/agents/${agentId}/plane-member`, {
        plane_member_id: memberId,
        plane_member_email: member ? member.email : null,
      });
      setAgents((prev) =>
        prev.map((a) =>
          a.id === agentId
            ? { ...a, plane_member_id: memberId, plane_member_email: member?.email || null }
            : a
        )
      );
    } catch (e) {
      setErr(String(e));
    } finally {
      setSaving((s) => ({ ...s, [agentId]: false }));
    }
  }

  const memberLabel = (id) => {
    if (!id) return 'Not mapped';
    const m = members.find((m) => m.id === id);
    return m ? (m.display_name || m.email) : id.slice(0, 8) + '…';
  };

  return (
    <main className="page">
      <div className="page__header">
        <span className="page__icon"><Icon name="agents" /></span>
        <div className="page__header-text">
          <h2>Agent Profiles</h2>
          <p>Map agents to Plane workspace members so routed work items are auto-assigned.</p>
        </div>
      </div>
      <RoleBanner action="change Plane member mappings" />
      {err && <div className="status status--error">{err}</div>}

      {loading ? (
        <p className="status status--muted">Loading…</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>ID</th>
                <th>Name</th>
                <th>Key</th>
                <th style={{ width: '100px' }}>Autonomy</th>
                <th style={{ width: '90px' }}>Pooled</th>
                <th style={{ width: '200px' }}>Plane Member</th>
                <th style={{ width: '90px' }}></th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 && (
                <tr><td colSpan={7}><EmptyState label="No agents found." /></td></tr>
              )}
              {agents.map((agent) => {
                const currentId = draft[agent.id] ?? agent.plane_member_id ?? '';
                const isDirty = currentId !== (agent.plane_member_id || '');
                return (
                  <tr key={agent.id}>
                    <td className="cell-strong">{agent.id}</td>
                    <td>{agent.name}</td>
                    <td><code className="inline-code">{agent.key}</code></td>
                    <td><Badge tone="info">L{agent.autonomy_level}</Badge></td>
                    <td><Badge tone={agent.pooled ? 'success' : 'muted'}>{agent.pooled ? 'yes' : 'no'}</Badge></td>
                    <td>
                      {members.length === 0 || !canOperate() ? (
                        <span className="cell-muted">{agent.plane_member_id ? memberLabel(agent.plane_member_id) : 'Not mapped'}</span>
                      ) : (
                        <select
                          className="field-select"
                          value={currentId}
                          onChange={(e) => setDraft((d) => ({ ...d, [agent.id]: e.target.value }))}
                        >
                          <option value="">— Not mapped —</option>
                          {members.map((m) => (
                            <option key={m.id} value={m.id}>{m.display_name || m.email}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {members.length > 0 && canOperate() && (
                        <button
                          type="button"
                          className="btn-primary btn-small"
                          disabled={!isDirty || saving[agent.id]}
                          onClick={() => saveMapping(agent.id)}
                        >
                          {saving[agent.id] ? 'Saving…' : 'Save'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {members.length === 0 && !loading && (
        <p className="panel__footnote">
          Plane not connected — member dropdown unavailable. Configure{' '}
          <code className="inline-code">PLANE_API_TOKEN</code> and restart the backend to enable mapping.
        </p>
      )}
    </main>
  );
}
